/**
 * Admin Landing Page.
 *
 * Entry point for all administration features. Displays navigation cards
 * linking to focused sub-pages: Settings, Team & Access, Workflows,
 * Finance, and Integrations.
 */

import { Link } from "react-router-dom";
import {
  Settings2,
  Users,
  ClipboardList,
  FileText,
  Zap,
  Receipt,
  Globe,
  ArrowRightLeft,
  Upload,
  ChevronRight,
  Shield,
  Workflow,
  DollarSign,
  Plug,
} from "lucide-react";
import Card from "@/components/ui/Card";

interface AdminCard {
  title: string;
  description: string;
  path: string;
  icon: React.ElementType;
  iconColor: string;
  features: Array<{ label: string; icon: React.ElementType }>;
}

const adminCards: AdminCard[] = [
  {
    title: "Settings",
    description:
      "Organization profile, products, locations, number formats, and role configuration.",
    path: "/admin/settings",
    icon: Settings2,
    iconColor:
      "bg-brand-200 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300",
    features: [
      { label: "Business Profile", icon: Settings2 },
      { label: "Products & Categories", icon: Settings2 },
      { label: "Locations", icon: Settings2 },
      { label: "Work Orders", icon: Settings2 },
      { label: "Sales", icon: Settings2 },
      { label: "Roles", icon: Shield },
    ],
  },
  {
    title: "Team & Access",
    description:
      "Manage users, permissions, and review the audit trail of all system activity.",
    path: "/admin/team",
    icon: Users,
    iconColor:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300",
    features: [
      { label: "User Management", icon: Users },
      { label: "Activity Log", icon: ClipboardList },
    ],
  },
  {
    title: "Workflows",
    description:
      "Build processes, create custom forms, and set up automation rules.",
    path: "/admin/workflows",
    icon: Workflow,
    iconColor:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
    features: [
      { label: "Processes", icon: FileText },
      { label: "Forms", icon: FileText },
      { label: "Automations", icon: Zap },
    ],
  },
  {
    title: "Finance",
    description:
      "Configure tax and fee schemes, certificate rules, and currency exchange rates.",
    path: "/admin/finance",
    icon: DollarSign,
    iconColor:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
    features: [
      { label: "Tax & Fee Schemes", icon: Receipt },
      { label: "Exchange Rates", icon: ArrowRightLeft },
    ],
  },
  {
    title: "Integrations",
    description:
      "Connect to third-party services and manage data import/export.",
    path: "/admin/integrations",
    icon: Plug,
    iconColor: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300",
    features: [
      { label: "Integrations", icon: Globe },
      { label: "Import / Export", icon: Upload },
    ],
  },
];

function AdminHub() {
  return (
    <div className="page-content space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Administration
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Organization settings, users, workflows, and integrations.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {adminCards.map((card) => (
          <Link key={card.path} to={card.path} className="group">
            <Card className="h-full transition-all duration-150 hover:shadow-md hover:border-brand-200 dark:hover:border-brand-800">
              <div className="flex items-start gap-4">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-xl)] ${card.iconColor}`}
                >
                  <card.icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-[var(--text-primary)] group-hover:text-brand-600 transition-colors">
                      {card.title}
                    </h3>
                    <ChevronRight className="h-4 w-4 text-[var(--text-tertiary)] group-hover:text-brand-600 transition-all group-hover:translate-x-0.5" />
                  </div>
                  <p className="mt-1 text-xs text-[var(--text-secondary)] leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {card.features.map((f) => (
                  <span
                    key={f.label}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--color-surface-secondary)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-secondary)]"
                  >
                    {f.label}
                  </span>
                ))}
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default AdminHub;
